import * as React from "react";
import {translate} from "react-i18next";
import i18n from "i18next";
import {ITranslatedComponent, ITypefamilyMap} from "../../redux/reducers/generalReducer";
import {TypefamilyDto, ModelyearDto, ResourceListOfModelyearDto} from "../../services/Client";
import {Table, Checkbox} from "semantic-ui-react";
import DependentSelect, {IDependentSelectDataAdapter, IDependentSelectFilter, EDirection} from "../../components/DependentSelect";
import Button from "../../components/Button";
import Dialog from "../../components/Dialogs/Dialog";
import ColumnFactory from "../../services/ColumnFactory";
import { ICopyPSL } from "../OptionsView/PSL/PslCopy";
import deepEqual from "deep-equal";

export interface IModelyearTypefamilyFilter extends IDependentSelectFilter {
    sourceModelyear?: ModelyearDto;
    sourceTypefamily?: TypefamilyDto;
    targetModelyear?: ModelyearDto;
    targetTypefamily?: TypefamilyDto;
}

interface IModeltypeCopyDialogProps extends ITranslatedComponent {
    /**
     * Called when the user clicks on copy of context
     *
     * @param value
     *      information about source and target contexts
     * @param withSequence
     *      whether or not to also copy the sequence
     */
    onCopyModeltype(value: IModelyearTypefamilyFilter, withSequence: boolean): void;

    /**
     * called when the user clicks on cancel
     */
    onCancel?(): void;

    /** Model years to which the user has access */
    userModelyears: ResourceListOfModelyearDto;

    /** All available typefamilies */
    typefamilyMap?: ITypefamilyMap;

    onChange?(copyPSL: ICopyPSL): void;
    value?: IModelyearTypefamilyFilter;
    isCopyOkClicked:boolean;
}

/** data adapter for modelyears */
class ModelyearAdapter implements IDependentSelectDataAdapter {
    getFilter(): IModelyearTypefamilyFilter {
        return this.adapter.getFilter();
    }
    renderer?: (option: ModelyearDto, index?: number) => string;

    constructor(private adapter: ModeltypeCopyDialog, private key: string) {
        this.renderer = (option: ModelyearDto, index?: number) => ColumnFactory.mj2str(option);
        this.setFilter = this.setFilter.bind(this);
        this.getData = this.getData.bind(this);
    }
    getKey(): string {
        return this.key;
    }
    getData(): ModelyearDto[] {
        return this.adapter.getData(this.key) as ModelyearDto[];
    }
    setFilter(filter: IModelyearTypefamilyFilter): Promise<void> {
        return this.adapter.setFilter(filter);
    }
}

/** data adapter for typefamilies */
class TypefamilyAdapter implements IDependentSelectDataAdapter {
    getFilter(): IModelyearTypefamilyFilter {
        return this.adapter.getFilter();
    }
    renderer?: (option: TypefamilyDto, index?: number) => string;

    constructor(private adapter: ModeltypeCopyDialog, private key: string) {
        this.renderer = (typefamily: TypefamilyDto, index?: number) => i18n.t("GLOBAL:" + typefamily.key);
        this.setFilter = this.setFilter.bind(this);
        this.getData = this.getData.bind(this);
    }
    getKey(): string {
        return this.key;
    }
    getData(): TypefamilyDto[] {
        return this.adapter.getData(this.key) as TypefamilyDto[];
    }
    setFilter(filter: IModelyearTypefamilyFilter): Promise<void> {
        return this.adapter.setFilter(filter);
    }
}

interface IModeltypeCopyDialogState {
    dataAdaptersSrc: Array<IDependentSelectDataAdapter>;
    dataAdaptersTgt: Array<IDependentSelectDataAdapter>;
    curSelection: IModelyearTypefamilyFilter;

    // whether or not to also copy the sequence
    withSequence: boolean;
}

/**
 * shows the dialog to fill the properties for a new modeltype
 */
class ModeltypeCopyDialog extends React.Component<IModeltypeCopyDialogProps, IModeltypeCopyDialogState>
{
    constructor(props: IModeltypeCopyDialogProps) {
        super(props);
        this.state = {
            dataAdaptersSrc: [
                new ModelyearAdapter(this, "sourceModelyear"),
                new TypefamilyAdapter(this, "sourceTypefamily")
            ],
            dataAdaptersTgt: [
                new ModelyearAdapter(this, "targetModelyear"),
                new TypefamilyAdapter(this, "targetTypefamily")
            ],
            curSelection: props.value ? props.value : {},
            withSequence: false
        };

        this.handleCopyModeltype = this.handleCopyModeltype.bind(this);
        this.handleCancel = this.handleCancel.bind(this);

        this.getData = this.getData.bind(this);
        this.getFilter = this.getFilter.bind(this);
        this.setFilter = this.setFilter.bind(this);
        this.getTypefamily = this.getTypefamily.bind(this);
    }

    componentWillReceiveProps(nextProps: Readonly<IModeltypeCopyDialogProps>) {
        if (!deepEqual(nextProps.value, this.props.value, {strict: true})) {
            this.setFilter(nextProps.value ? nextProps.value : {});
        }
        if(nextProps.isCopyOkClicked !== this.props.isCopyOkClicked) {
            this.handleCopyModeltype();
        }
    }

    getFilter(): IModelyearTypefamilyFilter {
        return this.state.curSelection;
    }

    setFilter(filter: IModelyearTypefamilyFilter): Promise<void> {
        const {typefamilyMap, userModelyears} = this.props;
        const sel = Object.assign({}, this.state.curSelection, filter);

        if (sel.sourceModelyear !== undefined) {
            const modelyears: ModelyearDto[] = userModelyears.elements ? userModelyears.elements : [];
            sel.sourceModelyear = modelyears.find(value => value.mj1 === sel.sourceModelyear!.mj1);
        }
        else {
            sel.sourceModelyear = undefined;
        }

        if (sel.sourceModelyear !== undefined && sel.targetModelyear !== undefined && sel.sourceModelyear.mj1 === sel.targetModelyear.mj1) {
            sel.targetModelyear = undefined;
        }
        if (sel.sourceModelyear !== undefined && sel.sourceTypefamily !== undefined) {
            const typefamilySource = this.getTypefamily(sel.sourceModelyear);
            sel.sourceTypefamily = typefamilySource.find(value => value.typefamily === sel.sourceTypefamily!.typefamily);
        }
        if (sel.sourceModelyear !== undefined && sel.targetModelyear !== undefined && sel.sourceTypefamily !== undefined && typefamilyMap) {
            const typefamilyTarget = this.getTypefamily(sel.targetModelyear);
            sel.targetTypefamily = typefamilyTarget.find(value => value.typefamily === sel.sourceTypefamily!.typefamily);
        }
        else {
            sel.targetTypefamily = undefined;
        }
        this.setState({curSelection: sel}, () => {
            if (this.props.onChange) {
                this.props.onChange(sel);
            }
        });
        return Promise.resolve();
    }

    getTypefamily(modelyear: ModelyearDto | undefined): TypefamilyDto[] {
        const {typefamilyMap} = this.props;
        const typefamilyRes = typefamilyMap && modelyear ? typefamilyMap[modelyear.mj1] : undefined;
        return typefamilyRes && typefamilyRes.elements ? typefamilyRes.elements : [];
    }

    getData(key: string): (ModelyearDto | TypefamilyDto)[] {
        const {userModelyears} = this.props;
        const {curSelection} = this.state;
        const modelyears: ModelyearDto[] = userModelyears.elements ? userModelyears.elements : [];

        const typefamilySource = this.getTypefamily(curSelection.sourceModelyear);

        let result: (ModelyearDto | TypefamilyDto)[];
        switch (key) {
            case "sourceModelyear":
                result = modelyears;
                break;
            case "targetModelyear":
                result = curSelection.sourceModelyear !== undefined ? modelyears.filter(value => value.mj1 !== curSelection.sourceModelyear!.mj1) : [];
                break;
            case "sourceTypefamily":
                result = typefamilySource;
                break;
            case "targetTypefamily":
                result = curSelection.targetTypefamily ? [curSelection.targetTypefamily] : [];
                break;
            default:
                result = [];
                break;
        }
        return result;
    }

    private handleCopyModeltype() {
        const {curSelection, withSequence} = this.state;
        if (this.props.onCopyModeltype
            && curSelection
            && curSelection.sourceModelyear
            && curSelection.sourceTypefamily
            && curSelection.targetModelyear
            && curSelection.targetTypefamily) {
            this.props.onCopyModeltype(curSelection, withSequence);
        }
    }

    private handleCancel() {
        if (this.props.onCancel) {
            this.props.onCancel();
        }
    }

    render() {
        const {userModelyears, t} = this.props;

        const {dataAdaptersSrc, dataAdaptersTgt, curSelection} = this.state;
        const placeholders = [t("MODELYEAR"), t("TYPEFAMILY")];

        const negSrc = !curSelection.sourceModelyear || !curSelection.sourceTypefamily;
        const negTgt = !curSelection.targetModelyear || !curSelection.targetTypefamily;

        const disabled = negSrc || negTgt;
        const content = (
            <Table>
                <Table.Body>
                    <Table.Row>
                        <Table.Cell width="1" negative={negSrc}>
                            <DependentSelect
                                title={t("SOURCE")}
                                titleStyle={{textAlign: 'left', fontWeight: 'normal'}}
                                direction={EDirection.Vertical}
                                placeholders={placeholders}
                                dataAdapters={dataAdaptersSrc}
                                value={curSelection}
                            />
                        </Table.Cell>
                        <Table.Cell width="1" negative={negTgt}>
                            <DependentSelect
                                title={t("TARGET")}
                                titleStyle={{textAlign: 'left', fontWeight: 'normal'}}
                                readonlies={[curSelection.sourceModelyear === undefined, true]}
                                direction={EDirection.Vertical}
                                placeholders={placeholders}
                                dataAdapters={dataAdaptersTgt}
                                value={curSelection}
                            />
                        </Table.Cell>
                    </Table.Row>
                    <Table.Row>
                        <Table.Cell colSpan={'2'}>
                            <Checkbox
                                label={t("COPY_WITH_SEQUENCE")}
                                checked={this.state.withSequence}
                                onChange={() => this.setState({withSequence: !this.state.withSequence})}
                            />
                        </Table.Cell>
                    </Table.Row>
                </Table.Body>
            </Table>
        );

        const btnCopy = <Button key={1} icon={"checkmark"} disabled={disabled} onClick={this.handleCopyModeltype} />;
        const btnCancel = <Button key={2} icon={"ban"} onClick={this.handleCancel} />;
        let actions =
            [
                btnCopy,
                btnCancel,
            ];
        return (
             <>
               {userModelyears.elements && userModelyears.elements.length > 0
                    ? content
                    : <>{this.props.t("MISSING_ACCESS_RIGHTS")}</>
                }
             </>
        );
    }
}

export default translate(["MT_BASIC_VIEW", "COLUMN_FACTORY"])(ModeltypeCopyDialog);



// WEBPACK FOOTER //
// ./src/views/ModeltypeBasicView/ModeltypeCopyDialog.tsx